import _extends from "@babel/runtime/helpers/extends";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { KeyboardUtilsReact } from "abreact/incubator/ui/utils/KeyboardUtilsReact";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbHSeparator } from "abreact/layout/AbHSeparator";
import { AbMenuButton } from "abreact/menu/AbMenuButton";
import { AbButton } from "abreact/misc/AbButton";
import { AbIcon } from "abreact/misc/AbIcon";
import { AbItem } from "abreact/misc/AbItem";
import { AbSize, AbSizeContext, sizeProviderWrapper } from "abreact/misc/AbSize";
import { AbBusyIndicator } from "abreact/progress/AbBusyIndicator";
import { AbTreeUtils } from "abreact/tree/AbTreeUtils";
import { useCurrentValue, useOnUnmount, useSynchronizedCallback, useUniqueId } from "abreact/utils/AbHooks";
import { AbRouteLink } from "absdk/preliminary/commons/app/router/AbRouteLink";
import { AB_ITEM_SEPARATOR } from "abweb/collection/AbItemSeparator";
import { AbKeyMap } from "abweb/collection/AbKeyMap";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { AbAsyncUtils } from "abweb/utils/async/AbAsyncUtils";
import { AbMaybePromise } from "abweb/utils/async/AbMaybePromise";
import * as React from "react";

/**
 * The properties for an individual menu button item shown in the **AbMenu** component.
 */

/**
 * The properties for the **AbMenu** component.
 */

/**
 * A menu of actions that can be shown to the user.
 */
export function AbMenu(props) {
  const {
    syncItems,
    cachePath,
    closeEntireMenu
  } = useCachedMenuItems(props);
  const useEmptyIcon = !!syncItems && syncItems.some(item => item !== AB_ITEM_SEPARATOR && item.icon !== undefined);
  const menuDivRef = React.useRef(null);
  const getEnabledMenuButtons = () => {
    return Array.from(menuDivRef.current?.querySelectorAll(".ab-menu-link:not(.ab-disabled), .ab-menu-item.ab-button:not(.ab-disabled)") ?? []);
  };
  const contextSize = React.useContext(AbSizeContext);

  // This handles autoFocus. When the items mount, if autoFocus is set, the first enabled button takes focus.
  const tookFocusRef = React.useRef(false);
  React.useEffect(() => {
    if (!tookFocusRef.current && props.autoFocus && syncItems) {
      tookFocusRef.current = true;
      const items = getEnabledMenuButtons();
      if (items.length > 0) items[0].focus();
    }
    // disable exhaustive-deps: we only want to run this effect if syncItems changes from defined to undefined.
  }, [syncItems === undefined]); // eslint-disable-line abinitio/exhaustive-deps

  const handleNavigationEvent = e => {
    if (e.defaultPrevented) return;
    e.preventDefault();
    const currentMenuButton = e.target;
    const menuButtons = getEnabledMenuButtons();
    const thisIndex = menuButtons.findIndex(element => element === currentMenuButton);
    if (thisIndex >= 0) {
      let nextButton;
      if ([KeyValue.ARROW_UP, KeyValue.ARROW_LEFT].includes(e.key)) {
        nextButton = menuButtons[(thisIndex + menuButtons.length - 1) % menuButtons.length];
      } else {
        nextButton = menuButtons[(thisIndex + 1) % menuButtons.length];
      }
      nextButton.focus();
    }
  };
  const keyHandler = KeyboardUtilsReact.makeKeyHandler([[KeyValue.ARROW_UP, handleNavigationEvent], [KeyValue.ARROW_DOWN, handleNavigationEvent]]);
  React.useImperativeHandle(props.abRef, () => ({
    focusFirstItem: () => {
      const menuButtons = getEnabledMenuButtons();
      if (menuButtons.length === 0) return;
      menuButtons[0].focus();
    },
    focusLastItem: () => {
      const menuButtons = getEnabledMenuButtons();
      if (menuButtons.length === 0) return;
      menuButtons[menuButtons.length - 1].focus();
    },
    isFirstItemFocused: () => {
      const menuButtons = getEnabledMenuButtons();
      if (menuButtons.length === 0) return false;
      return document.activeElement === menuButtons[0];
    },
    isLastItemFocused: () => {
      const menuButtons = getEnabledMenuButtons();
      if (menuButtons.length === 0) return false;
      return document.activeElement === menuButtons[menuButtons.length - 1];
    }
  }));
  const renderMenuItems = () => {
    if (!syncItems) return /*#__PURE__*/React.createElement(AbBusyIndicator, null);
    if (syncItems.length === 0) return /*#__PURE__*/React.createElement("div", {
      className: "ab-menu-item ab-italic ab-menu-no-items"
    }, AbReactResources.menu_noItems());
    return syncItems.map((menuItem, index) => renderMenuItem(props, menuItem, index, cachePath, closeEntireMenu, useEmptyIcon));
  };
  const inner = /*#__PURE__*/React.createElement("div", _extends({}, ReactUtils.componentProps("ab-menu", props, [AbSize.SMALL]), {
    onKeyDown: e => {
      props.eventHandlers?.onKeyDown?.(e);
      keyHandler(e);
    },
    ref: menuDivRef
  }), renderMenuItems());
  return sizeProviderWrapper(inner, {
    size: AbSize.SMALL
  }, contextSize);
}
export const useClearCache = (isRoot, id) => {
  useOnUnmount(() => {
    if (isRoot) {
      const keys = [...AbMenuCache.keys()];
      keys.forEach(key => {
        if (key[0] === id) {
          AbMenuCache.delete(key);
        }
      });
    }
  });
};
export const useCachedMenuItems = props => {
  const cacheChecked = React.useRef(false);
  const uniqueId = useUniqueId("menu-button");
  const {
    path: contextPath,
    closeEntireMenu
  } = React.useContext(AbMenuContext);
  const skipCachingRef = useCurrentValue(props.skipCaching);
  const menuCacheId = contextPath ?? [uniqueId];
  const isRoot = contextPath === undefined;
  const [cacheItems, itemsAreCaching] = useSynchronizedCallback(async stale => {
    cacheChecked.current = true;
    const cachedItems = AbMenuCache.get(menuCacheId);
    // If functions are equal and last function gave me a promise, use cache, do not re-call
    if (cachedItems !== AbKeyMap.NOT_FOUND && cachedItems.dataFreshness === props.dataFreshness && cachedItems.isAsync && cachedItems.items === props.items) return;
    AbMenuCache.delete(menuCacheId);
    const calcMenuItems = async () => {
      if (AbAsyncUtils.isCalculatedFunc(props.items)) {
        const calculatedItems = props.items();
        if (AbMaybePromise.isPromise(calculatedItems)) {
          const awaitedItems = await calculatedItems;
          return {
            menuItems: awaitedItems,
            isAsync: true
          };
        }
        return {
          menuItems: calculatedItems,
          isAsync: false
        };
      }
      return {
        menuItems: props.items,
        isAsync: false
      };
    };
    const {
      menuItems,
      isAsync
    } = await calcMenuItems();
    const truthyItems = TypeUtils.filterOutNullishOrFalse(menuItems);
    if (stale.stale) return;
    AbMenuCache.set(menuCacheId, {
      syncItems: truthyItems,
      items: props.items,
      dataFreshness: props.dataFreshness,
      isAsync
    });
  });
  React.useEffect(() => {
    if (skipCachingRef.current || ArrayUtils.isArrayLike(props.items)) return;
    cacheItems();
  }, [props.items, props.dataFreshness]);
  useClearCache(isRoot, menuCacheId[0]);
  const calcSyncItems = () => {
    if (ArrayUtils.isArrayLike(props.items)) return TypeUtils.filterOutNullishOrFalse(props.items);
    const cachedItems = AbMenuCache.get(menuCacheId);
    const cacheNotDone = cachedItems === AbKeyMap.NOT_FOUND || itemsAreCaching || !cacheChecked.current;
    if (cacheNotDone) return undefined;
    return cachedItems.syncItems;
  };
  return {
    syncItems: calcSyncItems(),
    cachePath: menuCacheId,
    isRoot: isRoot,
    closeEntireMenu: closeEntireMenu
  };
};
export const AbMenuCache = AbKeyMap.from([], path => {
  return AbTreeUtils.makePathKeyFunction(undefined)(path);
});
export const AbMenuContext = /*#__PURE__*/React.createContext({
  closeEntireMenu: undefined,
  path: undefined
});
AbMenuContext.displayName = "AbMenuPathContext";
function renderMenuItem(props, menuItem, index, pathPrefix, closeEntireMenu, useEmptyIcon) {
  if (menuItem === AB_ITEM_SEPARATOR) {
    return /*#__PURE__*/React.createElement(AbHSeparator, {
      key: index
    });
  }
  const key = menuItem.key ?? TypeUtils.itemToKey(menuItem, undefined);
  const path = [...pathPrefix, key];
  const defaultedIcon = menuItem.icon ?? (useEmptyIcon ? "absdk:transparent" : undefined);
  if (menuItem.customRenderer) {
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: key
    }, menuItem.customRenderer({
      icon: defaultedIcon,
      className: "ab-no-border-button ab-menu-item"
    }));
  }
  if (itemIsRouteLink(menuItem)) {
    return /*#__PURE__*/React.createElement(AbRouteLink, _extends({}, menuItem, {
      className: ReactUtils.classNamesString([menuItem.className, "ab-menu-link", "ab-no-wrap", "ab-with-hover"]),
      icon: defaultedIcon,
      key: key,
      onClick: () => {
        closeEntireMenu?.();
      }
    }));
  }

  // At this point, menuItem is guaranteed to be defined and of type AbMenuItemProps
  const {
    icon,
    menuItems,
    className,
    label,
    onClick,
    ...restOfButtonProps
  } = menuItem;
  const calcHasSubmenu = () => {
    if (menuItems === undefined) return false;
    if (ArrayUtils.isArrayLike(menuItems)) {
      const truthyItems = TypeUtils.filterOutNullishOrFalse(menuItems);
      return truthyItems.length > 0;
    }
    const cachedValue = AbMenuCache.get(path);
    if (cachedValue === AbKeyMap.NOT_FOUND) return true;
    return cachedValue.syncItems.length > 0;
  };
  const hasSubmenu = calcHasSubmenu();
  const wrappedOnClick = event => {
    onClick?.(event);
    closeEntireMenu?.();
  };
  const classes = ReactUtils.classNamesString([className, "ab-no-border-button", "ab-menu-item"]);
  const wrappedLabel = /*#__PURE__*/React.createElement("div", {
    className: "ab-menu-item-label"
  }, defaultedIcon ? /*#__PURE__*/React.createElement(AbItem, {
    icon: defaultedIcon,
    label: menuItem.label
  }) : menuItem.label, hasSubmenu && /*#__PURE__*/React.createElement(AbIcon, {
    className: "ab-submenu-button",
    image: "absdk:chevron-right",
    altText: ""
  }));
  const sharedProps = {
    ...restOfButtonProps,
    className: classes,
    label: wrappedLabel
  };
  if (hasSubmenu && menuItems) {
    return /*#__PURE__*/React.createElement(AbMenuContext.Provider, {
      value: {
        path: path,
        closeEntireMenu: closeEntireMenu
      },
      key: key
    }, /*#__PURE__*/React.createElement(AbMenuButton, _extends({}, sharedProps, {
      dataFreshness: props.dataFreshness,
      onClick: onClick,
      menuProps: {
        className: props.className,
        style: props.style
      },
      menuItems: menuItems
    })));
  } else if (onClick !== undefined) {
    return /*#__PURE__*/React.createElement(AbButton, _extends({}, sharedProps, {
      key: key,
      onClick: wrappedOnClick
    }));
  } else {
    return /*#__PURE__*/React.createElement("div", _extends({}, ReactUtils.componentProps("ab-menu-item", menuItem), {
      key: key
    }), wrappedLabel);
  }
}
const itemIsRouteLink = item => {
  return "concreteRoute" in item;
};
//# sourceMappingURL=AbMenu.js.map
